import { MatrixClient } from 'matrix-js-sdk/src/matrix';

import { listScheduledMeetings, MeetingType } from 'api/meedioBackend/listScheduledMeetings';
import { MeetingResponse } from 'api/meedioBackend/types';
import { updateMeeting } from 'api/meedioBackend/updateMeeting';
import { isEndedScheduledMeeting } from 'modules/Meetings/utils/isEndedScheduledMeeting';
import logger from 'utils/logging/faro';
import { inviteUsersToRoom } from 'utils/matrixUtils';

export type SyncLinkedMeetingInvitesResult = {
  syncedMeetingIds: string[];
  failedMeetingIds: string[];
};

export const filterEligibleLinkedMeetings = (meetings: MeetingResponse[], messengerRoomId: string): MeetingResponse[] =>
  meetings.filter(
    (meeting) =>
      meeting.messenger_room_id === messengerRoomId &&
      !meeting.cancelled_at &&
      !isEndedScheduledMeeting(meeting) &&
      !!meeting.matrix_room_id
  );

/**
 * Registers mxids on every eligible chat-linked scheduled meeting and invites
 * them to the meeting Matrix room. Never throws — aggregates per-meeting failures.
 */
export const syncLinkedScheduledMeetingInvites = async (
  matrixClient: MatrixClient,
  messengerRoomId: string,
  mxids: string[]
): Promise<SyncLinkedMeetingInvitesResult> => {
  const result: SyncLinkedMeetingInvitesResult = {
    syncedMeetingIds: [],
    failedMeetingIds: [],
  };

  if (mxids.length === 0) {
    return result;
  }

  let meetings: MeetingResponse[];
  try {
    meetings = await listScheduledMeetings({
      messengerRoomId,
      type: MeetingType.Scheduled,
    });
  } catch (error) {
    logger.warn('syncLinkedScheduledMeetingInvites: listScheduledMeetings failed', {
      messengerRoomId,
      mxids,
      error,
    });
    return result;
  }

  const eligibleMeetings = filterEligibleLinkedMeetings(meetings, messengerRoomId);

  for (const meeting of eligibleMeetings) {
    try {
      await updateMeeting(meeting.id, { add_guest_mxids: mxids });
      await inviteUsersToRoom(matrixClient, meeting.matrix_room_id, mxids);
      result.syncedMeetingIds.push(meeting.id);
    } catch (error) {
      logger.warn('syncLinkedScheduledMeetingInvites: failed to sync meeting', {
        meetingId: meeting.id,
        messengerRoomId,
        mxids,
        error,
      });
      result.failedMeetingIds.push(meeting.id);
    }
  }

  return result;
};
